<template>
  <div class="c-led-pcb" v-visible="!loading">
    <img class="background" :src="images.led_in_switch" @load="onLoad" />
    <div class="leds">
      <div
        v-for="(color, index) in frameRGB"
        class="led"
        :class="{
          disabled: isPlaying,
        }"
        :key="color + index"
        :style="styles[index]"
        @mousedown.left="onMouseDownLeft({ color, index })"
        @mousedown.right="onMouseDownRight({ color, index })"
        @mouseenter="onMouseEnter({ color, index }, $event)"
        @contextmenu.prevent
      >
        <div class="content">
          <div
            class="background"
            :style="{
              backgroundColor: color,
            }"
          ></div>
          <div
            class="center"
            :style="{
              backgroundColor: color,
              boxShadow: `0 0 0.45rem 0.2rem ${color}`,
              opacity: 1 || getLight(color),
            }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { speedList } from "@/utils";

export default {
  name: "CLedPcb",
  props: {
    isPlaying: Boolean,
    speed: Number,
    frames: Array,
    frameIndex: Number,
  },
  data() {
    return {
      loading: true,
      playingIndex: 0,
      styles: [
        { content: "LED_REC", display: "none" },
        { content: "LED_ESC", marginRight: "3.25%" },
        { content: "LED_F1" },
        { content: "LED_F2" },
        { content: "LED_F3" },
        { content: "LED_F4", marginRight: "3.25%" },
        { content: "LED_F5" },
        { content: "LED_F6" },
        { content: "LED_F7" },
        { content: "LED_F8", marginRight: "3.25%" },
        { content: "LED_F9" },
        { content: "LED_F10" },
        { content: "LED_F11" },
        { content: "LED_F12", marginRight: "3.25%" },
        { content: "LED_DELETE", marginBottom: "1.6%" },
        // 14
        { content: "LED_GAT" },
        { content: "LED_1" },
        { content: "LED_2" },
        { content: "LED_3" },
        { content: "LED_4" },
        { content: "LED_5" },
        { content: "LED_6" },
        { content: "LED_7" },
        { content: "LED_8" },
        { content: "LED_9" },
        { content: "LED_0" },
        { content: "LED_MIS" },
        { content: "LED_EQU", marginRight: "4.85%" },
        { content: "LED_BACKSPACE", marginRight: "5%" },
        // 28
        { content: "LED_TAB", marginLeft: "1.6%", marginRight: "3.25%" },
        { content: "LED_Q" },
        { content: "LED_W" },
        { content: "LED_E" },
        { content: "LED_R" },
        { content: "LED_T" },
        { content: "LED_Y" },
        { content: "LED_U" },
        { content: "LED_I" },
        { content: "LED_O" },
        { content: "LED_P" },
        { content: "LED_OQO" },
        { content: "LED_EQO", marginRight: "3.25%" },
        { content: "LED_BSL" },
        // 42
        { content: "LED_CAPSLOCK", marginLeft: "2.4%", marginRight: "4%" },
        { content: "LED_A" },
        { content: "LED_S" },
        { content: "LED_D" },
        { content: "LED_F" },
        { content: "LED_G" },
        { content: "LED_H" },
        { content: "LED_J" },
        { content: "LED_K" },
        { content: "LED_L" },
        { content: "LED_COL" },
        { content: "LED_CC", marginRight: "5.7%" },
        { content: "LED_ENTER", marginRight: "5.5%" },
        { content: "LED_PAGEDOWN", marginRight: "0" },
        // 56
        { content: "LED_LEFTSHIFT", marginLeft: "4%", marginRight: "5.7%" },
        { content: "LED_Z" },
        { content: "LED_X" },
        { content: "LED_C" },
        { content: "LED_V" },
        { content: "LED_B" },
        { content: "LED_N" },
        { content: "LED_M" },
        { content: "LED_CMA" },
        { content: "LED_DOT" },
        { content: "LED_SL", marginRight: "20%" },
        {
          content: "LED_PAGEUP",
          position: "absolute",
          right: "0",
          top: "37.3%",
          marginRight: "0",
        },
        {
          content: "LED_END",
          position: "absolute",
          right: "0",
          top: "21%",
          marginRight: "0",
        },
        {
          content: "LED_HOME",
          position: "absolute",
          right: "0",
          top: "0",
          marginRight: "0",
        },
        // 70
        { content: "LED_LEFTCTRL", marginLeft: "0.8%", marginRight: "3.3%" },
        { content: "LED_LEFTGUI", marginRight: "3.3%" },
        { content: "LED_LEFTALT", marginRight: "3.3%" },
        { content: "LED_LSPACEBAR", marginRight: "13.1%", width: "2.6%" },
        { content: "LED_SPACEBAR", marginRight: "13.1%" },
        { content: "LED_RSPACEBAR", marginRight: "3.5%", width: "2.6%" },
        { content: "LED_FN", marginRight: "3.3%" },
        { content: "LED_RIGHTCTRL", marginRight: "5.45%" },
        { content: "LED_LEFT" },
        { content: "LED_DOWN" },
        { content: "LED_RIGHT", marginRight: "0" },
        {
          content: "LED_RIGHTSHIFT",
          position: "absolute",
          right: "13.4%",
          top: "70.5%",
        },
        {
          content: "LED_UP",
          position: "absolute",
          right: "4.7%",
          top: "70.5%",
        },
      ],
    };
  },
  computed: {
    ...mapState("assets", {
      images: "images",
    }),
    frameRGB() {
      if (this.frames) {
        const index = this.isPlaying ? this.playingIndex : this.frameIndex;
        return this.frames[index] ? this.frames[index].frameRGB : [];
      }
      return [];
    },
  },
  watch: {
    isPlaying: function (isPlaying) {
      if (isPlaying) {
        this.play();
      }
    },
  },
  methods: {
    getLight(hex) {
      const regs = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

      let r = parseInt(regs[1], 16);
      let g = parseInt(regs[2], 16);
      let b = parseInt(regs[3], 16);

      (r /= 255), (g /= 255), (b /= 255);

      const max = Math.max(r, g, b);
      const light = max * 2;
      return light.toFixed(3);
    },
    play() {
      this.playingIndex = 0;
      clearTimeout(this.timer);
      if (this.isPlaying) {
        this.timer = setInterval(() => {
          this.playingIndex = this.playingIndex + 1;
          if (this.playingIndex >= this.frames.length) this.playingIndex = 0;
        }, speedList[this.speed - 1] - 2);
      }
    },
    onLoad() {
      this.loading = false;
    },
    onMouseDownLeft({ color, index }) {
      this.$emit("led-click", { color, index });
    },
    onMouseDownRight({ index }) {
      this.$emit("led-click-right", { index });
    },
    onMouseEnter({ color, index }, { which, buttons }) {
      if (buttons === undefined ? which === 1 : buttons === 1) {
        this.$emit("led-click", { color, index });
      } else if (buttons === undefined ? which === 3 : buttons === 2) {
        this.$emit("led-click-right", { index, solid: true });
      }
    },
  },
  mounted() {
    if (this.isPlaying) this.play();
  },
};
</script>

<style lang="scss" scoped>
.c-led-pcb {
  width: 100%;
  position: relative;

  .background {
    width: 100%;
    height: auto;
    position: relative;
    -webkit-user-drag: none;
  }

  .leds {
    width: 88%;
    height: 77.3%;
    position: absolute;
    left: 6%;
    top: 7.6%;
    display: flex;
    flex-wrap: wrap;
    align-content: space-between;

    .led {
      width: 4.7%;
      height: 13%;
      margin-right: 1.66%;
      flex: none;
      cursor: pointer;

      &:hover .content .background {
        // display: block;
      }

      &.disabled {
        background: transparent;
        pointer-events: none;
        cursor: auto;
      }

      .content {
        width: 100%;
        height: 100%;
        position: relative;

        .background {
          width: 100%;
          height: 100%;
          position: absolute;
          display: none;
        }

        .center {
          width: 8px;
          height: 8px;
          left: 50%;
          top: 75%;
          margin-left: -4px;
          border-radius: 50%;
          position: absolute;
          filter: brightness(120%);
        }
      }
    }
  }
}
</style>
